/**
 * Communities, their hostnames, their people, and the permission model.
 *
 * One D1 database with `community_id` on every tenant-scoped table (D5). The isolation that
 * matters is not enforced here — it is enforced in `packages/domain`, where a repository cannot
 * be constructed without a `TenantContext`. What this file contributes is the hostname table
 * that resolution reads from, which is the reason a URL path segment is never a security
 * boundary: the tenant comes from a validated hostname lookup, not from the request path.
 */

import { sql } from "drizzle-orm";
import { index, integer, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import { archivable, audited, currency, flag, id, instant, json, ref } from "./columns.js";

export const COMMUNITY_STATUSES = ["active", "suspended", "archived"] as const;

export const communities = sqliteTable(
  "communities",
  {
    id: id(),
    slug: text("slug").notNull(),
    name: text("name").notNull(),
    legalName: text("legal_name"),

    /** What the public site says when it differs from the registered name. Null falls back to `name`. */
    publicName: text("public_name"),
    shortName: text("short_name"),
    description: text("description"),

    /** Poster-facing contact. A phone number is the primary channel, not an afterthought (D21). */
    contactEmail: text("contact_email"),
    contactPhone: text("contact_phone"),
    mailingAddress: text("mailing_address"),
    website: text("website"),

    /**
     * Province or territory, as a subdivision code — `ON`, `MB`, `SK`.
     *
     * Named `region` rather than `province` to match `events.region` and `participants.region`,
     * which already carry the same fact under that name; a third spelling for one concept is how a
     * query ends up searching two columns. It is the coarsest thing a person browsing the directory
     * filters by, which is why it is a column and not a key in `brandingJson`.
     */
    region: text("region"),
    country: text("country").notNull().default("CA"),

    /** IANA zone. A configuration value, never a guess — see decisions.md, Assumptions. */
    timezone: text("timezone").notNull(),
    defaultLocale: text("default_locale").notNull().default("en-CA"),
    /** Locales offered beyond the default, as a JSON string array. */
    supportedLocalesJson: json("supported_locales_json"),
    currency: currency(),

    /** Colours and logos. Validated on read; a malformed brand falls back to the default. */
    brandingJson: json("branding_json"),

    /**
     * The community's own words for platform concepts (`@powwow/localization`). Present from the
     * start rather than retrofitted, because renaming a concept later means finding every string.
     */
    terminologyJson: json("terminology_json"),

    status: text("status", { enum: COMMUNITY_STATUSES }).notNull().default("active"),
    ...audited(),
    ...archivable(),
  },
  (table) => [uniqueIndex("communities_slug_unique").on(table.slug)],
);

/**
 * Hostname → community. The lookup that resolves a tenant.
 *
 * `{slug}.pow-wow.ca` is a row here rather than a parse of the subdomain, so onboarding a
 * community is a database write and a community can later add a custom domain without any code
 * knowing the difference.
 */
export const communityDomains = sqliteTable(
  "community_domains",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    hostname: text("hostname").notNull(),
    isPrimary: flag("is_primary").notNull().default(false),
    verifiedAt: instant("verified_at"),
    ...audited(),
  },
  (table) => [
    uniqueIndex("community_domains_hostname_unique").on(table.hostname),
    index("community_domains_community_idx").on(table.communityId),
  ],
);

/**
 * The one authority in the system that reaches across communities.
 *
 * Everything else — even `owner` — is scoped to a single community by `permission_grants`, which is
 * the whole tenancy design (D5, and `tenant.ts`). A platform administrator is deliberately the
 * exception and deliberately outside that model: it grants no permission *inside* any community, it
 * only permits the two things that necessarily sit above every community — creating one, and
 * listing them all. A platform admin who wants to run an event still receives an ordinary
 * community grant like anyone else.
 *
 * Its own table rather than a flag on `users`, so "who can create communities" is one small,
 * greppable, auditable list rather than a column that has to be read on every user load. Recorded
 * as decision D44.
 */
export const platformAdmins = sqliteTable(
  "platform_admins",
  {
    id: id(),
    userId: ref("user_id").notNull(),
    ...audited(),
  },
  (table) => [uniqueIndex("platform_admins_user_unique").on(table.userId)],
);

export const USER_STATUSES = ["active", "suspended"] as const;

/**
 * A person. `email` is nullable, which is the single most consequential column in the schema:
 * a judge picked at the drum has a name and nothing else (D15), and a four-year-old dancer has
 * a guardian's phone number (D21).
 */
export const users = sqliteTable(
  "users",
  {
    id: id(),
    name: text("name").notNull(),
    preferredName: text("preferred_name"),
    email: text("email"),
    phone: text("phone"),
    locale: text("locale"),
    status: text("status", { enum: USER_STATUSES }).notNull().default("active"),
    ...audited(),
    ...archivable(),
  },
  // Unique only where present. Several users legitimately have no email at all, and a plain
  // unique index would let exactly one of them exist.
  (table) => [
    uniqueIndex("users_email_unique")
      .on(table.email)
      .where(sql`email is not null`),
  ],
);

export const CREDENTIAL_TYPES = ["magic_link", "oauth", "access_code", "password"] as const;

/**
 * Credentials, separated from users so one person can hold a staff magic link all year and a
 * session-scoped judging code for one afternoon without the two interfering (D15).
 *
 * An `access_code` is short, numeric and single-binding: the first device to present it sets
 * `bound_device_id`, and every later presentation from a different device is rejected. That is
 * narrower than an emailed link rather than weaker — it is scoped to one session, self-expiring,
 * and revocable in one tap.
 */
export const userCredentials = sqliteTable(
  "user_credentials",
  {
    id: id(),
    userId: ref("user_id").notNull(),
    type: text("type", { enum: CREDENTIAL_TYPES }).notNull(),

    /** Email for `magic_link`, provider subject for `oauth`, null for `access_code`. */
    identifier: text("identifier"),
    /** Never the secret itself. Access codes are hashed like any other credential. */
    secretHash: text("secret_hash"),
    provider: text("provider"),

    /** Scope of an access code: the session it was issued for. Null for staff credentials. */
    sessionId: ref("session_id"),

    expiresAt: instant("expires_at"),
    boundDeviceId: text("bound_device_id"),
    boundAt: instant("bound_at"),
    revokedAt: instant("revoked_at"),
    lastUsedAt: instant("last_used_at"),

    /**
     * The password must be replaced before this credential grants anything.
     *
     * Set on every administrator-issued temporary password, so a password someone else chose can
     * open the door exactly once and cannot become the account's standing credential. The sign-in
     * route honours it by issuing a session that is only good for setting a new password.
     */
    mustReset: flag("must_reset").notNull().default(false),

    /**
     * Consecutive failed attempts against this credential, and when to stop refusing.
     *
     * Kept on the credential rather than in KV because the lockout has to survive a cold edge and
     * apply wherever the request lands — a per-isolate counter is not a lockout, it is a delay for
     * whoever is unlucky with routing.
     */
    failedAttempts: integer("failed_attempts").notNull().default(0),
    lockedUntil: instant("locked_until"),
    ...audited(),
  },
  (table) => [
    index("user_credentials_user_idx").on(table.userId),
    index("user_credentials_lookup_idx").on(table.type, table.identifier),
    index("user_credentials_session_idx").on(table.sessionId),
    /**
     * One live access code per judge per session.
     *
     * A code opens every category its holder is assigned in that session, so a second live one is
     * never a narrower credential — it is an interchangeable duplicate that staff read out as if
     * it named something, and that keeps working after the code it replaced was meant to die.
     * Both mint paths already retire the old code first; this is what makes that true of paths
     * nobody has written yet, and of two staff assigning the same judge at the same moment.
     *
     * Partial, on the two conditions that make a code usable: revoking is what retires one, so a
     * revoked row must not block its replacement. The scope is the session, not the event — a
     * judge working Saturday and Sunday holds one code for each, by design (D15).
     */
    uniqueIndex("user_credentials_live_access_code_unique")
      .on(table.userId, table.sessionId)
      .where(sql`type = 'access_code' and revoked_at is null`),
  ],
);

export const authSessions = sqliteTable(
  "auth_sessions",
  {
    id: id(),
    userId: ref("user_id").notNull(),
    /** Null for a platform-level session that has not yet chosen a community. */
    communityId: ref("community_id"),
    credentialId: ref("credential_id"),
    tokenHash: text("token_hash").notNull(),
    deviceId: text("device_id"),
    expiresAt: instant("expires_at").notNull(),
    revokedAt: instant("revoked_at"),
    lastSeenAt: instant("last_seen_at").notNull(),
    ...audited(),
  },
  (table) => [
    uniqueIndex("auth_sessions_token_unique").on(table.tokenHash),
    index("auth_sessions_user_idx").on(table.userId),
  ],
);

export const invitations = sqliteTable(
  "invitations",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    email: text("email").notNull(),
    roleId: ref("role_id").notNull(),
    tokenHash: text("token_hash").notNull(),
    expiresAt: instant("expires_at").notNull(),
    /** Stamped on every send, including resends, so "was this ever delivered" is answerable. */
    lastSentAt: instant("last_sent_at"),
    acceptedAt: instant("accepted_at"),
    acceptedBy: ref("accepted_by"),
    revokedAt: instant("revoked_at"),
    ...audited(),
  },
  (table) => [
    uniqueIndex("invitations_token_unique").on(table.tokenHash),
    index("invitations_community_idx").on(table.communityId),
  ],
);

export const MEMBER_STATUSES = ["active", "suspended", "left"] as const;

export const communityMembers = sqliteTable(
  "community_members",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    userId: ref("user_id").notNull(),
    status: text("status", { enum: MEMBER_STATUSES }).notNull().default("active"),
    joinedAt: instant("joined_at").notNull(),
    ...audited(),
  },
  (table) => [
    uniqueIndex("community_members_unique").on(table.communityId, table.userId),
    index("community_members_user_idx").on(table.userId),
  ],
);

/**
 * A role. `community_id` is null for the system roles every community starts with; a community
 * may also define its own, which is why the column exists at all.
 */
export const roles = sqliteTable(
  "roles",
  {
    id: id(),
    communityId: ref("community_id"),
    key: text("key").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    isSystem: flag("is_system").notNull().default(false),
    ...audited(),
  },
  (table) => [uniqueIndex("roles_scope_key_unique").on(table.communityId, table.key)],
);

export const permissions = sqliteTable(
  "permissions",
  {
    id: id(),
    key: text("key").notNull(),
    description: text("description").notNull(),
    ...audited(),
  },
  (table) => [uniqueIndex("permissions_key_unique").on(table.key)],
);

export const rolePermissions = sqliteTable(
  "role_permissions",
  {
    id: id(),
    roleId: ref("role_id").notNull(),
    permissionId: ref("permission_id").notNull(),
    ...audited(),
  },
  (table) => [uniqueIndex("role_permissions_unique").on(table.roleId, table.permissionId)],
);

export const GRANT_SCOPES = [
  "community",
  "event",
  "session",
  "competition",
  "category",
  "phase",
] as const;

/**
 * A role granted to a person, optionally narrowed to part of the tree.
 *
 * `session` is on the scope list because a judge's authority is bounded by the day, not by the
 * event (D15). A panel that changes between Saturday afternoon and Saturday evening is the
 * normal path here, not a workaround.
 */
export const permissionGrants = sqliteTable(
  "permission_grants",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    userId: ref("user_id").notNull(),
    roleId: ref("role_id").notNull(),
    scopeType: text("scope_type", { enum: GRANT_SCOPES }).notNull().default("community"),
    scopeId: ref("scope_id"),
    grantedBy: ref("granted_by"),
    expiresAt: instant("expires_at"),
    revokedAt: instant("revoked_at"),
    ...audited(),
  },
  (table) => [
    index("permission_grants_user_idx").on(table.communityId, table.userId),
    index("permission_grants_scope_idx").on(table.scopeType, table.scopeId),
  ],
);

/**
 * The audit trail. Written by repository hooks and route middleware rather than scattered call
 * sites, so it cannot be forgotten at one of them.
 *
 * `reason` is populated wherever a workflow demands a justification — a category override, a
 * registration into a closed window, a voided payout. That asymmetry is deliberate: speed where
 * volume lives, friction where money and fairness live (ux.md §2).
 */
export const auditLog = sqliteTable(
  "audit_log",
  {
    id: id(),
    communityId: ref("community_id"),
    actorUserId: ref("actor_user_id"),
    actorType: text("actor_type").notNull(),
    actorId: text("actor_id"),
    action: text("action").notNull(),
    entityType: text("entity_type").notNull(),
    entityId: text("entity_id").notNull(),
    beforeJson: json("before_json"),
    afterJson: json("after_json"),
    reason: text("reason"),
    ip: text("ip"),
    userAgent: text("user_agent"),
    createdAt: instant("created_at").notNull(),
  },
  (table) => [
    index("audit_log_entity_idx").on(table.entityType, table.entityId),
    index("audit_log_community_time_idx").on(table.communityId, table.createdAt),
  ],
);
